import { useState, useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { valuationLabels } from "@/i18n";
import { RegistrationInput } from "@/components/valuation/RegistrationInput";
import { VehicleDetailsForm } from "@/components/valuation/VehicleDetailsForm";
import { ExpectationsForm } from "@/components/valuation/ExpectationsForm";
import { Button } from "@/components/ui/button";
import { X, Info } from "lucide-react";
import { VehicleDetails } from "@/types/vehicleDetails";
import { useValuation } from "@/hooks/useValuation";
import { convertAutoUncleToVehicleDetails, toVehicleDetailsPatch } from "@/selectors/autoUncleSelectors";
import { isVehicleInfoComplete } from "@/utils/vehicleInfoCompletion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AutoUncleVerificationDialog } from "@/components/car-post-ad/AutoUncleVerificationDialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useIsMobile } from "@/hooks/use-mobile";
import { LicensePlate } from "@/components/my-auctions/LicensePlate";
import { useAuth } from "@/hooks/useAuth";

interface CarValuationWidgetProps {
  onClose?: () => void;
  showCloseButton?: boolean;
  className?: string;
  embedMode?: boolean;
}

export const CarValuationWidget = ({
  onClose,
  showCloseButton = false,
  embedMode = false,
}: CarValuationWidgetProps) => {
  const [step, setStep] = useState<number>(0);
  const [vehicleDetails, setVehicleDetails] = useState<Partial<VehicleDetails>>(
    {
      equipment: [],
    },
  );
  const [regNumber, setRegNumber] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const [isAutoLoaded, setIsAutoLoaded] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isManualMode, setIsManualMode] = useState(false);
  const [formResetKey, setFormResetKey] = useState(0);
  const { getVehicleInformationAsync } = useValuation();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const isGuest = !isAuthLoading && !isAuthenticated;
  const isMobile = useIsMobile();
  const { language } = useLanguage();
  const vLabels = valuationLabels(language);
  const STEP_LABELS = [
    vLabels.steps.search,
    vLabels.steps.vehicleInfo,
    vLabels.steps.expectedPrice,
  ];
  const STEP_LABELS_MOBILE = [
    vLabels.steps.search,
    vLabels.steps.vehicleInfoShort,
    vLabels.steps.price,
  ];

  // AutoUncle verification popup state
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [verificationData, setVerificationData] = useState<{
    transmission?: string;
    engineVolume?: string;
    fuelType?: string;
    bodyType?: string;
    doors?: number;
    /** Drives the MC exemption for the engine-volume check. */
    vehicleType?: "car" | "mc";
  } | null>(null);

  // Safety measure: Warn user when trying to navigate away after step 0
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (step >= 1) {
        e.preventDefault();
        return vLabels.leaveConfirm;
      }
    };

    const handlePopState = (e: PopStateEvent) => {
      if (step >= 1) {
        const confirmed = window.confirm(vLabels.leaveConfirm);
        if (!confirmed) {
          e.preventDefault();
          window.history.pushState(null, "", window.location.href);
        }
      }
    };

    if (step >= 1) {
      window.addEventListener("beforeunload", handleBeforeUnload);
      window.addEventListener("popstate", handlePopState);
      window.history.pushState(null, "", window.location.href);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [step, vLabels.leaveConfirm]);

  // Emit progress events for navigation components
  useEffect(() => {
    const event = new CustomEvent("carValuationProgress", {
      detail: { hasProgress: step >= 1 },
    });
    window.dispatchEvent(event);
  }, [step]);

  // Guest (auth ready): load Turnstile + prefetch while user types regnr
  useEffect(() => {
    if (!isGuest || step !== 0) return;
    let cancelled = false;
    void import("@/lib/turnstile").then(
      ({ isTurnstileConfigured, preloadTurnstile, prefetchTurnstileToken }) => {
        if (cancelled || !isTurnstileConfigured()) return;
        preloadTurnstile();
        prefetchTurnstileToken();
      },
    );
    return () => {
      cancelled = true;
    };
  }, [isGuest, step]);

  const handleRegNumberSubmit = async (regNumber: string) => {
    if (!regNumber || regNumber.trim() === "") return;
    // Wait for AuthContext bootstrap so logged-in users are not treated as guests
    if (isAuthLoading) return;

    setLoading(true);
    setNotFound(false);
    setRateLimited(false);
    setShowSuccess(false);
    setRegNumber(regNumber);

    try {
      let turnstileToken: string | undefined;
      // Logged-in users skip Turnstile (BE also skips when soft-auth attaches user)
      if (isGuest) {
        const { getTurnstileToken, isTurnstileConfigured } = await import(
          "@/lib/turnstile"
        );
        if (isTurnstileConfigured()) {
          turnstileToken = await getTurnstileToken();
          if (!turnstileToken) {
            throw new Error("TURNSTILE_EMPTY");
          }
        }
      }

      const result = await getVehicleInformationAsync({
        lpn: regNumber,
        turnstileToken,
      });

      // Check if result is empty or undefined
      if (!result || !result.car) {
        throw new Error("Vehicle information not found");
      }

      // Check if critical fields are missing - but be more lenient
      // Only return early if ALL THREE critical fields are missing
      const hasBrand = result.car.brand && result.car.brand !== "";
      const hasModel = result.car.car_model && result.car.car_model !== "";
      const hasYear =
        result.car.year &&
        result.car.year !== 0 &&
        result.car.year !== null &&
        result.car.year !== undefined;

      const missingAllCriticalFields = !hasBrand && !hasModel && !hasYear;

      if (missingAllCriticalFields) {
        // DEBUG: Uncomment for debugging missing data issues
        // console.warn("[CarValuation] ⚠️ Missing all critical fields, using minimal data");
        setVehicleDetails({
          regNumber: result.car.lpn,
          vin: result.car.vin,
          engineVolume: result.car.engineVolume,
        });
        setIsAutoLoaded(true);
        setIsManualMode(false); // Not manual mode when data is loaded
        setLoading(false);
        setShowSuccess(true);

        setTimeout(() => {
          setShowSuccess(false);
          setStep(1);
        }, 1000);
        return;
      }

      // The backend envelope already matches the selector's expected shape.
      const carInfoForSelector = {
        ...result.car,
        km: result.car.km || 0,
        variant: result.car.variant || "",
        doors: result.car.doors,
      };

      const convertedDetails = convertAutoUncleToVehicleDetails(
        carInfoForSelector as any,
      );

      // IMPORTANT: Elbilar är alltid automatiska, så sätt "Automat" direkt för elbilar
      const isElectric =
        result.car.fuel === "El" ||
        result.car.fuel === "Elbil" ||
        result.car.fuel === "ELECTRIC";
      if (isElectric) {
        convertedDetails.transmission = "Automat";
      }

      // Shared mapper - see toVehicleDetailsPatch. Hand-enumerating the field list here
      // is what silently dropped newly added lookup fields.
      const vehicleDetails: Partial<VehicleDetails> = {
        ...toVehicleDetailsPatch(carInfoForSelector as any),
        ...(isElectric ? { transmission: "Automat" } : {}),
      };

      // Vehicle found - populate form with data
      // DEBUG: Uncomment for debugging data mapping issues
      // console.log("[CarValuation] 📝 Final vehicleDetails before setState:", {
      //   make: vehicleDetails.make,
      //   model: vehicleDetails.model,
      //   variant: vehicleDetails.variant,
      //   year: vehicleDetails.year,
      //   fuelType: vehicleDetails.fuelType,
      //   bodyType: vehicleDetails.bodyType,
      //   transmission: vehicleDetails.transmission,
      //   color: vehicleDetails.color,
      //   engineVolume: vehicleDetails.engineVolume,
      //   horsepower: vehicleDetails.horsepower,
      //   doors: vehicleDetails.doors,
      //   mileage: vehicleDetails.mileage,
      //   equipment: vehicleDetails.equipment,
      //   vin: vehicleDetails.vin,
      //   regNumber: vehicleDetails.regNumber,
      //   isElectric,
      // });

      // IMPORTANT: Set isAutoLoaded FIRST, then vehicleDetails
      // This ensures useVehicleForm hook can properly detect the auto-load trigger
      setIsAutoLoaded(true);
      // IMPORTANT: Set ALL data at once - use functional update to ensure we don't lose any data
      setVehicleDetails((prev) => {
        return {
          ...prev,
          ...vehicleDetails,
        };
      });
      setIsManualMode(false); // Not manual mode when data is loaded
      setLoading(false);

      // Store verification data for later (when user clicks "Nästa" in details section)
      // Check if engine volume needs verification for non-electric vehicles
      // Trigger verification if engine volume is missing, invalid, or suspiciously low (< 0.8L)
      const engineVolumeNum = result.car.engineVolume
        ? parseFloat(result.car.engineVolume)
        : null;
      // Motorcycles legitimately have tiny engines (0.125L is a real moped engine),
      // so the "suspiciously low" heuristic must not fire for them.
      const isMc = result.car.vehicleType === "mc";
      const needsEngineVolumeVerification =
        !isElectric &&
        !isMc &&
        (engineVolumeNum === null ||
          isNaN(engineVolumeNum) ||
          engineVolumeNum < 0.8);

      if (needsEngineVolumeVerification) {
        setVerificationData({
          transmission: result.car.transmission,
          vehicleType: result.car.vehicleType,
          engineVolume: result.car.engineVolume,
          fuelType: result.car.fuel,
          bodyType: result.car.body,
          doors: result.car.doors,
        });
      } else {
        setVerificationData(null);
      }

      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
        setStep(1);
      }, 1000);
    } catch (error) {
      // The backend signals "no vehicle data - let the user type it in" with an error
      // status carrying doManualSearch (code DO_MANUAL_SEARCH). The shared axios client
      // preserves the response body on `details` and lifts `code` onto `errorCode`.
      //
      // It is deliberately an error status rather than 200-with-flag so that older
      // compiled-in mobile bundles, which know nothing about this flag, keep their
      // existing error handling instead of rendering an empty form.
      const apiError = error as {
        status?: number | string;
        errorCode?: string | null;
        details?: { doManualSearch?: boolean };
      };
      const shouldManualSearch =
        apiError?.errorCode === "DO_MANUAL_SEARCH" ||
        apiError?.details?.doManualSearch === true;

      if (shouldManualSearch) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      if (
        apiError?.errorCode === "CARINFO_RATE_LIMITED" ||
        apiError?.errorCode === "TURNSTILE_REQUIRED" ||
        apiError?.errorCode === "TURNSTILE_FAILED" ||
        Number(apiError?.status) === 429 ||
        Number(apiError?.status) === 403
      ) {
        setRateLimited(true);
        setLoading(false);
        return;
      }

      // Client-side Turnstile failure (script/challenge) — same soft UX as rate limit
      const message = error instanceof Error ? error.message : "";
      if (message.startsWith("TURNSTILE_")) {
        setRateLimited(true);
        setLoading(false);
        return;
      }

      setNotFound(true);
      setLoading(false);
    }
  };

  const handleRetrySearch = () => {
    setStep(0);
    setNotFound(false);
    setRateLimited(false);
    setRegNumber("");
    setIsAutoLoaded(false);
  };

  const handleManualEntry = () => {
    // Reset EVERYTHING for manual entry but stay on step 1 (Fordonsinfo)
    setStep(1); // Stay on Fordonsinfo step
    setNotFound(false);
    setRateLimited(false);
    setIsAutoLoaded(false);
    setIsManualMode(true); // Enable manual mode to show reg number input
    // Reset ALL vehicle details explicitly to ensure complete reset
    setVehicleDetails({
      equipment: [],
      make: undefined,
      model: undefined,
      year: undefined,
      fuelType: undefined,
      engine: undefined,
      bodyType: undefined,
      transmission: undefined,
      color: undefined,
      vehicleType: undefined,
      mileage: undefined,
      vin: undefined,
      regNumber: undefined,
      currentTireType: undefined,
      currentRimType: undefined,
    });
    setRegNumber(""); // Reset registration number
    // Force a complete re-mount of VehicleDetailsForm by updating key
    setFormResetKey((prev) => prev + 1);
  };

  const handleDetailsSubmit = (details: Partial<VehicleDetails>) => {
    // If manual mode and regNumber is provided, include it
    if (isManualMode && regNumber) {
      details.regNumber = regNumber;
    }
    // Update vehicleDetails immediately so summary card reflects changes
    setVehicleDetails(details);
  };

  const handleRegNumberChange = (newRegNumber: string) => {
    setRegNumber(newRegNumber);
    // Update vehicleDetails with regNumber when in manual mode
    if (isManualMode) {
      setVehicleDetails((prev) => ({
        ...prev,
        regNumber: newRegNumber,
      }));
    }
  };

  const handleNext = () => {
    // Check if engine volume verification is needed before advancing
    if (verificationData) {
      const engineVolumeNum = verificationData.engineVolume
        ? parseFloat(verificationData.engineVolume)
        : NaN;
      const isElectric =
        verificationData.fuelType === "El" ||
        verificationData.fuelType === "Elbil" ||
        verificationData.fuelType === "ELECTRIC";

      const needsEngineVolumeVerification =
        !isElectric &&
        verificationData.vehicleType !== "mc" &&
        (isNaN(engineVolumeNum) || engineVolumeNum < 0.8);

      if (needsEngineVolumeVerification) {
        setShowVerificationDialog(true);
        return; // Don't advance yet, wait for user to verify
      }
    }

    // No verification needed, advance to next step
    setStep(2);
  };

  const valuationHeaderPlate =
    step >= 1
      ? (vehicleDetails.regNumber || regNumber || "").toUpperCase().trim()
      : "";

  return (
    <div className={`relative pb-3`}>
      {showCloseButton && onClose && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="absolute -top-2 -right-2 z-10"
        >
          <X className="h-4 w-4" />
        </Button>
      )}

      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-4 sm:mb-5 w-full px-1">
        <h1 className="text-xl sm:text-2xl font-semibold text-center text-foreground">
          {vLabels.title}
        </h1>
        {valuationHeaderPlate ? (
          <LicensePlate className="shrink-0" lpn={valuationHeaderPlate} />
        ) : null}
      </div>

      <div className="flex justify-center gap-1 sm:gap-2 mb-4 sm:mb-6 w-full flex-wrap">
        {(isMobile ? STEP_LABELS_MOBILE : STEP_LABELS).map((label, idx) => {
          // Shared with the post-an-ad flow - see isVehicleInfoComplete. This gate used
          // to demand bodyType unconditionally, which permanently locked the progress
          // pill for motorcycles (MC mode clears bodyType).
          const allSectionsCompleted = isVehicleInfoComplete(vehicleDetails, {
            requireCondition: true,
          });

          const isLocked =
            (idx === 1 && !regNumber) || (idx === 2 && !allSectionsCompleted);
          const isCompleted =
            (idx === 0 && regNumber) ||
            (idx === 1 && allSectionsCompleted) ||
            (idx === 2 && step === 2);

          return (
            <button
              key={label}
              type="button"
              className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium transition whitespace-nowrap
                      ${
                        step === idx
                          ? "bg-[#D4AF37] text-white shadow"
                          : isCompleted
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-gray-300 hover:bg-[#D4AF37]/20"
                      }
                      ${
                        isLocked || loading
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }
                      `}
              onClick={() => {
                if (!isLocked && !loading) setStep(idx);
              }}
              disabled={isLocked || loading}
            >
              {label}
            </button>
          );
        })}
      </div>

      {step === 1 && isAutoLoaded && (
        <Alert className="mb-4 sm:mb-6 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 max-w-xl mx-auto">
          <Info className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          <AlertDescription className="text-amber-800 dark:text-amber-200">
            <span className="font-medium">
              {vLabels.search.autoLoadAlertImportant}
            </span>{" "}
            {vLabels.search.autoLoadAlertMessage}
          </AlertDescription>
        </Alert>
      )}

      {step === 0 && !loading && !notFound && !rateLimited && !showSuccess && (
        <RegistrationInput onSubmit={handleRegNumberSubmit} />
      )}

      {step === 0 && loading && (
        <div className="flex flex-col items-center justify-center py-8 sm:py-12">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-t-4 border-[#D4AF37] border-solid mb-3 sm:mb-4"></div>
          <span className="text-sm text-muted-foreground">
            {vLabels.search.loading}
          </span>
        </div>
      )}

      {step === 0 && showSuccess && (
        <div className="flex flex-col items-center justify-center py-8 sm:py-12">
          <div className="rounded-full h-10 w-10 sm:h-12 sm:w-12 bg-green-500 flex items-center justify-center mb-3 sm:mb-4">
            <svg
              className="h-5 w-5 sm:h-6 sm:w-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <span className="text-sm text-green-600 font-medium">
            {vLabels.search.successMessage}
          </span>
        </div>
      )}

      {step === 0 && rateLimited && (
        <div className="flex flex-col items-center justify-center py-6 sm:py-8 space-y-3 sm:space-y-4">
          <div className="text-center space-y-2">
            <p className="text-red-600 font-medium">
              {vLabels.search.rateLimitedTitle}
            </p>
            <p className="text-sm text-muted-foreground px-4">
              {vLabels.search.rateLimitedHint}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={handleRetrySearch}
              className="px-4 w-full sm:w-auto"
            >
              {vLabels.search.retryButton}
            </Button>
            <Button
              onClick={handleManualEntry}
              className="px-4 w-full sm:w-auto"
            >
              {vLabels.search.manualEntryButton}
            </Button>
          </div>
        </div>
      )}

      {step === 0 && notFound && !rateLimited && (
        <div className="flex flex-col items-center justify-center py-6 sm:py-8 space-y-3 sm:space-y-4">
          <div className="text-center space-y-2">
            <p className="text-red-600 font-medium">
              {vLabels.search.notFoundTitle}
            </p>
            <p className="text-sm text-muted-foreground px-4">
              {vLabels.search.notFoundHint}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={handleRetrySearch}
              className="px-4 w-full sm:w-auto"
            >
              {vLabels.search.retryButton}
            </Button>
            <Button
              onClick={handleManualEntry}
              className="px-4 w-full sm:w-auto"
            >
              {vLabels.search.manualEntryButton}
            </Button>
          </div>
        </div>
      )}

      {step === 1 && (
        <>
          <VehicleDetailsForm
            key={formResetKey}
            initialData={vehicleDetails}
            onChange={handleDetailsSubmit}
            onNext={handleNext}
            isAutoLoaded={isAutoLoaded}
            isManualMode={isManualMode}
            regNumber={regNumber}
            onRegNumberChange={handleRegNumberChange}
          />
        </>
      )}
      {step === 2 && (
        <ExpectationsForm vehicleInfo={vehicleDetails} embedMode={embedMode} />
      )}

      {/* AutoUncle Verification Dialog */}
      {verificationData && (
        <AutoUncleVerificationDialog
          open={showVerificationDialog}
          onClose={() => {
            setShowVerificationDialog(false);
            setVerificationData(null);
          }}
          onSave={(engineVolume) => {
            // Update vehicleDetails with verified engineVolume
            setVehicleDetails((prev) => ({
              ...prev,
              engineVolume:
                engineVolume !== undefined ? engineVolume : prev.engineVolume,
            }));
            setShowVerificationDialog(false);
            setVerificationData(null);

            // If we were trying to go to step 2, advance now after verification
            if (step === 1) {
              setStep(2);
            }
          }}
          initialEngineVolume={
            vehicleDetails.engineVolume || verificationData.engineVolume
          }
          showEngineVolume={
            verificationData.fuelType !== "El" &&
            verificationData.fuelType !== "Elbil" &&
            verificationData.fuelType !== "ELECTRIC" &&
            verificationData.vehicleType !== "mc" &&
            (!verificationData.engineVolume ||
              verificationData.engineVolume === "" ||
              parseFloat(verificationData.engineVolume) < 0.8)
          }
          vehicleDetails={vehicleDetails}
        />
      )}
    </div>
  );
};
